// Loose translator type to avoid i18next TFunction overload friction
type Translate = (key: string, options?: Record<string, unknown>) => unknown;

export interface ChairItem {
  id: string;
  image: string;
}

// Catalogue list (static id + image). Names come from translations: chairs.<id>.name
export const CHAIRS: ChairItem[] = [
  { id: 'loft', image: 'https://static.wixstatic.com/media/e733f0_4c1a0cb8d8674ce394564a0de76241ab~mv2.jpg/v1/fill/w_800,h_800,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/e733f0_4c1a0cb8d8674ce394564a0de76241ab~mv2.jpg' },
  { id: 'nova', image: '/nova.jpg.avif' },
  { id: 'belen', image: '/belen.jpg.avif' },
  { id: 'rona', image: '/rona.jpg.avif' },
  { id: 'nord', image: '/nord.jpg.avif' },
  { id: 'plus', image: '/plussinema.jpg.avif' },
  { id: 'mod', image: '/mod.jpg.avif' },
  { id: 'ares', image: '/ares.jpg.avif' },
  { id: 'vest', image: '/vest.jpg.avif' },
  { id: 'max', image: '/max.jpg.avif' },
  { id: 'giza', image: '/Giza.jpg.avif' },
  { id: 'jupiter', image: '/Jupiter.jpg.avif' },
  { id: 'venus', image: '/venus.avif' },
  { id: 'merkur', image: '/merkur.avif' },
  { id: 'mina', image: '/Mina.jpg.avif' },
  { id: 'mars', image: '/Mars.jpg.avif' },
  { id: 'eko', image: '/Okul.jpg.avif' },
  { id: 'etna', image: '/Etna.jpg.avif' },
  { id: 'vera', image: '/vera.jpg.avif' },
  { id: 'rast', image: '/rast.jpg.avif' },
  { id: 'efes', image: '/efes.jpg.avif' },
  { id: 'asus', image: '/asus.jpg.avif' },
  { id: 'martin', image: '/martin.jpg.avif' },
  { id: 'rom', image: '/rom.jpg.avif' },
];

export interface ChairSpecs {
  name: string;
  description: string;
  dimensions: { height: string; width: string; depth: string };
  technical: {
    structure: string;
    foam: string;
    upholstery: string;
    armrests: string;
    legStructure: string;
    seatingMechanism: string;
  };
  optional: string[];
  applications: string[];
  warranty: string;
}

export const getChairImage = (id: string): string =>
  CHAIRS.find((c) => c.id === id)?.image || '';

// Build chair specs from translations. Returns null if the chair has no data.
export const getChairSpecs = (t: Translate, chairId: string): ChairSpecs | null => {
  const chairKey = `chairs.${chairId}`;
  const name = t(`${chairKey}.name`, { defaultValue: '' }) as string;
  const description = t(`${chairKey}.description`, { defaultValue: '' }) as string;

  if (!name || !description) {
    return null;
  }

  const dimensions = {
    height: t(`${chairKey}.dimensions.height`, { defaultValue: '' }) as string,
    width: t(`${chairKey}.dimensions.width`, { defaultValue: '' }) as string,
    depth: t(`${chairKey}.dimensions.depth`, { defaultValue: '' }) as string,
  };

  const technical = {
    structure: t(`${chairKey}.technical.structure`, { defaultValue: '' }) as string,
    foam: t(`${chairKey}.technical.foam`, { defaultValue: '' }) as string,
    upholstery: t(`${chairKey}.technical.upholstery`, { defaultValue: '' }) as string,
    armrests: t(`${chairKey}.technical.armrests`, { defaultValue: '' }) as string,
    legStructure: t(`${chairKey}.technical.legStructure`, { defaultValue: '' }) as string,
    seatingMechanism: t(`${chairKey}.technical.seatingMechanism`, { defaultValue: '' }) as string,
  };

  const optional = t(`${chairKey}.optional`, { returnObjects: true, defaultValue: [] }) as string[];
  const applications = t(`${chairKey}.applications`, { returnObjects: true, defaultValue: [] }) as string[];
  const warranty = t(`${chairKey}.warranty`, { defaultValue: '' }) as string;

  return {
    name,
    description,
    dimensions,
    technical,
    optional: Array.isArray(optional) ? optional : [],
    applications: Array.isArray(applications) ? applications : [],
    warranty,
  };
};
